import { useEffect, useRef, useState, type DragEvent } from 'react';

export interface DragStackState {
  order: string[];
  setOrder: (order: string[]) => void;
  resetOrder: (order: string[]) => void;
  draggingIndex: number | null;
  dragOverIndex: number | null;
  handleDragStart: (
    index: number
  ) => (event: DragEvent<HTMLDivElement>) => void;
  handleDragOver: (index: number) => (event: DragEvent<HTMLDivElement>) => void;
  handleDrop: (index: number) => (event: DragEvent<HTMLDivElement>) => void;
  handleDragEnd: () => void;
}

/**
 * Manages draggable cards, keeps their order and handle drag events.
 * Order is initialised from `initialOrder`
 * onReorder is called whenever the order changes due to a drop event.
 */
export function useDragReorder(
  initialOrder: string[],
  onReorder?: (order: string[]) => void
): DragStackState {
  const [order, setOrder] = useState<string[]>(initialOrder);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const prevInitialRef = useRef(initialOrder);

  useEffect(() => {
    if (prevInitialRef.current !== initialOrder && initialOrder.length > 0) {
      prevInitialRef.current = initialOrder;
      setOrder(initialOrder);
    }
  }, [initialOrder]);

  const reset = () => {
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  // Set order to a new value, and update ref to avoid being overwrited
  const resetOrder = (newOrder: string[]) => {
    prevInitialRef.current = newOrder;
    setOrder(newOrder);
  };

  const handleDragStart =
    (index: number) => (event: DragEvent<HTMLDivElement>) => {
      dragIndexRef.current = index;
      setDraggingIndex(index);
      event.dataTransfer.effectAllowed = 'move';
    };

  const handleDragOver =
    (index: number) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (dragIndexRef.current === null || dragIndexRef.current === index)
        return;
      setDragOverIndex(index);
    };

  const handleDrop = (index: number) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === index) {
      reset();
      return;
    }
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(index, 0, moved);
      onReorder?.(next);
      return next;
    });
    reset();
  };

  return {
    order,
    setOrder,
    resetOrder,
    draggingIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd: reset,
  };
}
